import { useEffect, useMemo, useState } from 'react';

import { Box, Link } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import useSWR from 'swr';

import { financeConfigurationAtom } from '~atoms';
import { BillingsService } from '~features/finances';

import { useStyles } from './style';
import { Table, EmptyTable, Label } from '../../../../../components';
import {
	isEmptyArray,
	isFullArray,
	isUndefined,
	decimalAmount,
	getFinanceTableHeaders,
} from '../../../../../shared/utility';
import * as actions from '../../../../../store/actions';

interface UserFinanceProps {
	userId?: number;
	onFetchBillings?(...args: unknown[]): unknown;
	onUpdatePagedState?(...args: unknown[]): unknown;
	userBillings?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
	userDetails?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
}

const service = new BillingsService();

const UserFinance = (props: UserFinanceProps) => {
	const { userId, onFetchBillings, userBillings, onUpdatePagedState, userDetails } = props;
	const { t } = useTranslation();
	const financeConfiguration = useAtomValue(financeConfigurationAtom);

	const classes = useStyles();

	const [pageNumber, setPageNumber] = useState(1);
	const [pageSize, setPageSize] = useState(10);

	const fetchParameters = useMemo(
		() => ({
			page: pageNumber,
			pageSize: pageSize,
			excludeOneOffPayments: financeConfiguration.excludeOneOffPayments,
			userId: userId,
			includeCurrent: true,
		}),
		[pageNumber, pageSize, financeConfiguration.excludeOneOffPayments, userId]
	);
	const { data: userBillingsData, isLoading: userBillingsLoading } = useSWR(
		[service.basePath, fetchParameters],
		([_, args]) => service.getBillingsUnmapped(args)
	);

	const { data: userDetailsData } = userDetails;

	useEffect(() => {
		return () => {
			onUpdatePagedState('billings', null, false);
		};
	}, []);

	const handlePageChange = (newPage) => {
		setPageNumber(newPage);
	};

	const handlePageSizeChange = (newSize) => {
		setPageSize(newSize);
		setPageNumber(1);
	};

	const statusColor = {
		open: 'default',
		paid: 'success',
	};

	const statusText = {
		open: t('ui.open'),
		paid: t('ui.label.paid'),
	};

	const billingLink = (billing) => {
		return (
			<Link
				component={RouterLink}
				to={`/financeManagement/personal/${billing.start}/${billing.title}/billing/${userDetailsData?.name}/${userDetailsData?.id}`}
				state={{
					data: {
						...billing,
						userDetails: true,
						userReference: {
							id: userDetailsData?.id,
							name: userDetailsData?.name,
						},
					},
				}}
				variant='h6'
			>
				{billing.title}
			</Link>
		);
	};

	const headers = getFinanceTableHeaders();

	const tableHeaders =
		isEmptyArray(userBillingsData?.results) ?
			[{ name: 'period', content: t('ui.period') }]
		:	[...headers];

	const loadingBody =
		isUndefined(userBillingsData?.results) || userBillingsLoading ?
			Array(5)
				.fill(Array(tableHeaders?.length).fill())
				.map((arr) => arr.map(() => ({ loading: true })))
		:	null;

	const tableBody =
		isFullArray(userBillingsData?.results) ?
			userBillingsData.results.map((billing) => [
				{ content: <Box className={classes.periodContent}>{billingLink(billing)}</Box> },
				{ content: `${decimalAmount(billing.amount.subtotal, billing.amount)}` },
				{ content: `${decimalAmount(billing.amount.discount, billing.amount)}` },
				{
					content: `${decimalAmount(billing.amount.subtotal - billing.amount.discount, billing.amount)}`,
				},
				{ content: `${decimalAmount(billing.amount.priceVat, billing.amount)}` },
				{ content: `${decimalAmount(billing.amount.refund, billing.amount)}` },
				{ content: `${decimalAmount(billing.amount.totalPrice, billing.amount)}` },
				{ content: `${decimalAmount(billing.open, billing.amount)}` },
				{
					content: (
						<Label className={classes.status} type={statusColor[billing.status]}>
							{' '}
							{statusText[billing.status]}{' '}
						</Label>
					),
				},
			])
		:	null;

	const emptyBody =
		isEmptyArray(userBillingsData?.results) ?
			[
				[
					{
						content: <EmptyTable label={'views.userDetails.finance.emptyResults'} />,
					},
				],
			]
		:	null;

	return (
		<Box>
			<Table
				body={loadingBody || tableBody || emptyBody}
				data={userBillingsData ? userBillingsData.results : []}
				handlePageChange={handlePageChange}
				header={tableHeaders}
				loading={userBillingsLoading}
				page={pageNumber}
				rowsPerPage={pageSize}
				setRowsPerPage={handlePageSizeChange}
				noTitle={true}
				total={userBillingsData?.total ? userBillingsData.total : 0}
				isNotPaginate={isEmptyArray(userBillingsData?.results)}
			/>
		</Box>
	);
};

const mapStateToProps = (state) => {
	return {
		userBillings: state.paged.billings,
		userDetails: state.details.user,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		onFetchBillings: (page, filters, concat) =>
			dispatch(actions.fetchBillings(page, filters, concat)),
		onUpdatePagedState: (identifier, data, concat) =>
			dispatch(actions.updatePagedState(identifier, data, concat)),
	};
};
export default connect(mapStateToProps, mapDispatchToProps)(UserFinance);
